import { useEffect, useRef, useCallback } from 'react';
import { getNotificationCount, getNotifications } from '../components/services/notifications';

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minutes
const MAX_INACTIVE_CYCLES = 3; // intervals

export const useNotificationRefresh = (loggedIn, setNotificationCount, setNotifications) => {
    const intervalRef = useRef(null);
    const lastCountRef = useRef(0);
    const inactiveCyclesRef = useRef(0);

    const refreshNotifications = useCallback(async () => {
        if (!loggedIn) {
            return;
        }

        try {
            const countResponse = await getNotificationCount();
            if (countResponse?.success) {
                const newCount = countResponse.data;
                if (newCount !== lastCountRef.current) {
                    const notificationsResponse = await getNotifications();
                    if (notificationsResponse?.success) {
                        setNotifications(notificationsResponse.data);
                    }
                    lastCountRef.current = newCount;
                }

                setNotificationCount(newCount);
            }
        } catch (error) {
            console.error('Failed to refresh notifications:', error);
        }
    }, [loggedIn, setNotificationCount, setNotifications]);

    const scheduleRefresh = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        if (!loggedIn) {
            return;
        }

        intervalRef.current = setInterval(async () => {
            inactiveCyclesRef.current += 1;
            if (inactiveCyclesRef.current > MAX_INACTIVE_CYCLES) {
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
                return;
            }

            await refreshNotifications();
        }, REFRESH_INTERVAL);

        refreshNotifications();
    }, [loggedIn, refreshNotifications]);

    useEffect(() => {
        if (loggedIn) {
            inactiveCyclesRef.current = 0;
            scheduleRefresh();
        }

        // cleanup function
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [loggedIn, scheduleRefresh]);

    return { refreshNotifications };
};
